import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthShell, { GoogleButton, MicrosoftButton } from "@/components/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { apiError, track } from "@/lib/api";

export default function Signup() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({
    name: "", email: "", password: "", workspace_name: "",
    country: "US", currency: "USD", default_loaded_hourly_cost: 75, start_with_sample: true,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = "Create your account | NetLift"; track("signup_started"); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ ...form, default_loaded_hourly_cost: Number(form.default_loaded_hourly_cost) });
      track("signup_completed", { method: "email" });
      navigate("/app/onboarding");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create your NetLift workspace"
      subtitle="Start your 14-day free trial. No credit card required."
      footer={<>Already have an account? <Link to="/login" className="font-medium text-navy hover:underline">Sign in</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="signup-error">{error}</div>}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input id="name" required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="signup-name" />
          </div>
          <div>
            <Label htmlFor="ws">Workspace name</Label>
            <Input id="ws" required value={form.workspace_name} onChange={(e) => set("workspace_name", e.target.value)} data-testid="signup-workspace" />
          </div>
        </div>
        <div>
          <Label htmlFor="email">Work email</Label>
          <Input id="email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="signup-email" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" required minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="signup-password" />
        </div>
        <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5">
          <div>
            <p className="text-sm font-medium text-slate-700">Start with sample setup data</p>
            <p className="text-xs text-slate-400">Areas, tools, and example entries to explore.</p>
          </div>
          <Switch checked={form.start_with_sample} onCheckedChange={(v) => set("start_with_sample", v)} data-testid="signup-sample" />
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-editorial hover:bg-editorial/90" data-testid="signup-submit">
          {loading ? "Creating..." : "Create workspace"}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
        <span className="h-px flex-1 bg-slate-200" /> or <span className="h-px flex-1 bg-slate-200" />
      </div>
      <GoogleButton label="Sign up with Google" />
      <div className="mt-3">
        <MicrosoftButton label="Sign up with Microsoft" />
      </div>
    </AuthShell>
  );
}
